<template>
    <tbody
        style="position: relative;"
        v-bind:class="{'tx-table__td-no-top-border': actionDetails?.action?.type === 'dex:liquidity_asset_2'}"
    >
        <tr class="new-row-animation">
            <td>
                <div class="tx-table__cell">
                    <div class="tx-overview-action-type" v-bind:class="{ 'tx-overview-action-type--warning': type === 'change_wallet_key' }">
                        <div class="tx-overview-action-type__icon">
                            <event-overview-icon
                                v-bind:event="eventName"
                            />
                        </div>
                        {{ $t(`events.${eventName}`) }}
                    </div>
                </div>
            </td>
            <td class="tx-table-route-cell">
                <div class="tx-table__cell">
                    <template v-if="type === 'jetton_swap' || type === 'nft_transfer' || type === 'jetton_transfer' || type === 'jetton_mint'">
                        <!-- Exception for nft purchase event, when sender is undefined -->
                        <template
                            v-if="eventName === 'nft_purchase' && !processedAddressesFrom && actionDetails?.meta?.marketplace">
                            <ui-address v-if="actionDetails.meta.marketplace === 'fragment'" address="UQBAjaOyi2wGWlk-EDkSabqqnF-MrrwMadnwqrurKpkla4QB" v-on:mouseenter.native="highlightAddress('UQBAjaOyi2wGWlk-EDkSabqqnF-MrrwMadnwqrurKpkla4QB')" v-on:mouseleave.native="clearHighlightNode" />
                            <span style="text-transform: capitalize" v-else>{{ actionDetails.meta.marketplace }}</span>
                        </template>
                        <ui-address
                            v-else-if="processedAddressesFrom"
                            v-bind:class="{ 'ui-address-event-overview': event }"
                            v-bind:address="processedAddressesFrom" v-on:mouseenter.native="highlightAddress(processedAddressesFrom)" v-on:mouseleave.native="clearHighlightNode"
                            v-bind:type="actionDetails.source_type"
                        />
                        <div class="tx-table-action-icon" v-if="hasSource">
                            <arrow-right />
                        </div>
                        <ui-address
                            v-bind:class="{ 'ui-address-event-overview': event }"
                            class="ui-address-event-overview"
                            v-bind:address="processedAddressesTo" v-on:mouseenter.native="highlightAddress(processedAddressesTo)" v-on:mouseleave.native="clearHighlightNode"
                            v-bind:type="actionDetails.destination_type"
                        />
                    </template>
                    <template v-else-if="type === 'nft_mint'">
                        <!-- A mint has no sender of its own: show the minter taken
                             from the trace, and always the new owner. -->
                        <template v-if="mintSource">
                            <ui-address
                                v-bind:class="{ 'ui-address-event-overview': event }"
                                v-bind:address="mintSource" v-on:mouseenter.native="highlightAddress(mintSource)" v-on:mouseleave.native="clearHighlightNode"
                                v-bind:type="actionDetails.source_type"
                            />
                            <div class="tx-table-action-icon">
                                <arrow-right />
                            </div>
                        </template>
                        <ui-address
                            v-bind:class="{ 'ui-address-event-overview': event }"
                            v-bind:address="processedAddressesTo || processedAddressesFrom" v-on:mouseenter.native="highlightAddress(processedAddressesTo || processedAddressesFrom)" v-on:mouseleave.native="clearHighlightNode"
                            v-bind:type="processedAddressesTo ? actionDetails.destination_type : actionDetails.source_type"
                        />
                    </template>
                    <template v-else-if="type === 'contract_deploy' || (type === 'change_wallet_key' && !processedAddressesFrom)">
                        <ui-address
                            v-bind:class="{ 'ui-address-event-overview': event }"
                            v-bind:address="processedAddressesTo" v-on:mouseenter.native="highlightAddress(processedAddressesTo)" v-on:mouseleave.native="clearHighlightNode"
                            v-bind:type="actionDetails.destination_type"
                        />
                    </template>
                    <template v-else>
                        <ui-address
                            v-bind:class="{ 'ui-address-event-overview': event || type === 'ton_transfer' }"
                            v-bind:address="processedAddressesFrom" v-on:mouseenter.native="highlightAddress(processedAddressesFrom)" v-on:mouseleave.native="clearHighlightNode"
                            v-bind:type="actionDetails.source_type"
                        />
                        <div class="tx-table-action-icon" v-if="event || type === 'ton_transfer'">
                            <arrow-right />
                        </div>
                        <ui-address v-if="event || type === 'ton_transfer'"
                            v-bind:class="{ 'ui-address-event-overview': event || type === 'ton_transfer' }"
                            v-bind:address="processedAddressesTo" v-on:mouseenter.native="highlightAddress(processedAddressesTo)" v-on:mouseleave.native="clearHighlightNode"
                            v-bind:type="actionDetails.destination_type"
                        />
                    </template>
                </div>
            </td>

            <td class="column-wide tx-table-value-cell">
                <div class="tx-table__cell tx-table__cell--align-right">
                    <div class="tx-table__value">
                        <event-overview-badge
                            v-bind:type="type"
                            v-bind:actionDetails="actionDetails"
                        />
                        <!-- An encrypted comment has nothing to show or copy: a lock, as in the address history: -->
                        <div class="tx-table-comment-icon" v-if="isEncrypted">
                            <icon-encrypted-message v-once class="tx-table-operation-icon"/>
                        </div>
                        <!-- Payload lives in a tooltip behind the icon, like comments in the tx table;
                             a click puts it on the clipboard, so op codes can be grabbed right here: -->
                        <div class="tx-table-comment-icon" v-else-if="payloadText">
                            <ui-copy-button hide-icon
                                v-bind:copy="payloadText"
                                v-bind:successMessage="$t('tx.payload_copy_success')"
                                v-on:mouseenter.native="showPayload = true"
                                v-on:mouseleave.native="showPayload = false">
                                <icon-message class="tx-table-operation-icon"/>
                            </ui-copy-button>
                            <ui-tooltip
                                v-bind:value="payloadText"
                                position="left"
                                v-if="showPayload"
                            />
                        </div>
                    </div>
                </div>
            </td>
        </tr>
    </tbody>
</template>

<script>
import ArrowRight from '@img/icons/lucide/chevron-right.svg?vue';
import IconMessage from '@img/icons/tonscan/message-14.svg?vue';
import IconEncryptedMessage from '@img/icons/tonscan/encrypted-message.svg?vue';
import EventOverviewBadge from './EventOverviewBadge.vue';
import EventOverviewIcon from './EventOverviewIcon.vue';

export default {
    data() {
        return {
            showPayload: false,
        };
    },

    props: {
        action: Object,
        actionDetails: Object,
        type: String,
        event: Boolean,
    },

    computed: {
        // One line of payload for the tooltip: op code, comment, provider or dex.
        payloadText() {
            const details = this.actionDetails;
            if (!details) return '';
            if (this.type === 'call_contract' || this.type === 'contract_deploy') return details.op || '';
            if (['jetton_transfer', 'nft_transfer', 'ton_transfer'].includes(this.type)) {
                return details.encrypted ? '' : (details.message || '');
            }
            if (details.meta?.provider) return `Provider: ${details.meta.provider}`;
            if (details.dex) return `DEX: ${details.dex}`;
            return '';
        },

        isEncrypted() {
            return ['jetton_transfer', 'nft_transfer', 'ton_transfer'].includes(this.type)
                && !!this.actionDetails?.encrypted;
        },

        eventName() {
            const exceptions = ['nft_purchase', 'jetton_burn'];

            if (this.actionDetails?.event && exceptions.includes(this.actionDetails.event)) {
                return this.actionDetails.event;
            }

            return this.type;
        },

        processedAddressesFrom() {
            return this.actionDetails.from;
        },

        // Nothing is drawn on the left when the sender is unknown, so the arrow
        // has to go with it — otherwise the route reads as coming from nowhere.
        hasSource() {
            return !!this.processedAddressesFrom
                || (this.eventName === 'nft_purchase' && !!this.actionDetails?.meta?.marketplace);
        },

        // A minted item usually lands on the minter itself, and the same address
        // printed on both sides of an arrow says nothing.
        mintSource() {
            if (!this.processedAddressesTo || this.processedAddressesFrom === this.processedAddressesTo) {
                return null;
            }

            return this.processedAddressesFrom;
        },

        processedAddressesTo() {
            return this.actionDetails.to;
        },
    },

    methods: {
        highlightAddress(address) {
            if (address) this.$store.commit('setHighlightedNode', { address });
        },
        clearHighlightNode() {
            this.$store.commit('clearHighlightedNode');
        },
    },

    components: {
        IconMessage,
        IconEncryptedMessage,
        ArrowRight,
        EventOverviewBadge,
        EventOverviewIcon,
    },
};
</script>

<style lang="scss">
.tx-table-address-route {
    display: flex;
    align-items: center;
}

// Column budget: the amount and the route never shrink, the payload takes
// whatever is left and truncates with an ellipsis.
.tx-table td.tx-table-route-cell,
.tx-table th.tx-table-route-cell {
    width: 100%;
    white-space: nowrap;
}
.tx-table td.tx-table-value-cell,
.tx-table th.tx-table-value-cell {
    width: 1%;
    white-space: nowrap;
}
// The amount keeps a 26px slot after it for the payload icon; the header
// takes the same inset so "Value" lines up with the numbers, not the icons.
.tx-table th.tx-table-value-cell .tx-table__cell {
    padding-right: calc(18px + 26px);
}
.tx-table-value-cell .tx-table-comment-icon {
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;

    .ui-copy-button {
        display: block;
        width: 14px;
        height: 14px;
        cursor: pointer;
    }
}

.ui-address-event-overview {
    max-width: 180px;
}
@media (max-width: 599px) {
    .tx-table-action-icon {
        margin: 0 5px;
    }
}
.tx-table__td-no-top-border td,
.tx-table__td-no-top-border .tx-mobile-content{
    border-top: none!important;
}

@media (min-width: 600px) {
    .column-wide .tx-table__cell--align-right {
        font-weight: 500;
    }
}
</style>
